import { USD, magnitude } from '../../lib/format';
import { C, ink } from '../../lib/theme';
import type { WaterfallStep } from '../../lib/types';
import { ChartEmpty } from './shared';

export type WaterfallKey = 'perda_bruta' | 'anulado' | 'vdi' | 'perda_confirmada';

/**
 * Gráfico 2 — Bruto → Anulado → VDI → Confirmado como WATERFALL (PRD §9.2),
 * não barras soltas: a ponte desenha a subtração, em vez de obrigar o olho a
 * fazê-la. É o gráfico que não existe no Lovable e a razão de existir do Hub.
 *
 * 4 passos (pedido explícito, 2026-09-09) — antes "Ajuste por Exceção" era
 * um passo só, misturando correção manual (Anulado) com motivo de regra
 * ainda sem correção (VDI, Índice de Inconsistência). Agora cada queda tem
 * sua própria barra flutuante, empilhadas: VDI mais perto do topo (bruto),
 * Anulado logo abaixo, Confirmado no chão.
 *
 * Desenhado em CSS e não numa lib de chart porque waterfall exige barras
 * FLUTUANTES (o meio começa no topo da confirmada e sobe até a bruta) — em
 * Recharts isso vira uma barra invisível de base, mais frágil de manter.
 *
 * `activeKeys`/`onToggle` (2026-09-14) — a barra virou filtro clicável no
 * `ConfirmadosPanel` (substituiu os 4 tiles de resumo que existiam antes).
 * Opcionais de propósito: sem eles o gráfico continua só leitura, mesmo
 * comportamento de sempre — é o que `ConfirmadosPage.tsx` (rota legada)
 * ainda usa, sem precisar saber desse estado novo.
 */
export function WaterfallChart({ steps, activeKeys, onToggle }: {
  steps: WaterfallStep[];
  activeKeys?: Set<WaterfallKey>;
  onToggle?: (key: WaterfallKey) => void;
}) {
  if (steps.length === 0) return <ChartEmpty />;

  const bruta = magnitude(steps.find((s) => s.step === 'perda_bruta')?.value);
  const anulado = magnitude(steps.find((s) => s.step === 'anulado')?.value);
  const vdi = magnitude(steps.find((s) => s.step === 'vdi')?.value);
  const confirmada = magnitude(steps.find((s) => s.step === 'perda_confirmada')?.value);

  if (bruta === 0) return <ChartEmpty text="Nenhuma perda confirmada no período filtrado." />;

  // Tudo em % da perda bruta, que é o topo da escala. As duas barras de queda
  // flutuam empilhadas entre a confirmada (chão) e a bruta (teto): Anulado
  // primeiro (mais perto do chão), VDI por cima dele.
  const pct = (v: number) => (v / bruta) * 100;

  // Verde (Anulado) vs azul (VDI) — mesma cor que o Funil usa pros dois
  // buckets, pedido explícito de não deixar os dois parecidos entre si.
  const bars: Array<{ key: WaterfallKey; label: string; display: string; bottom: number; size: number; color: string; floating: boolean }> = [
    { key: 'perda_bruta', label: 'Bruto', display: USD(bruta), bottom: 0, size: pct(bruta), color: C.churn, floating: false },
    { key: 'anulado', label: 'Anulado', display: `− ${USD(anulado)}`, bottom: pct(confirmada), size: pct(anulado), color: C.green, floating: true },
    { key: 'vdi', label: 'VDI', display: `− ${USD(vdi)}`, bottom: pct(confirmada + anulado), size: pct(vdi), color: C.blueSoft, floating: true },
    { key: 'perda_confirmada', label: 'Perda Confirmada', display: USD(confirmada), bottom: 0, size: pct(confirmada), color: C.accent, floating: false },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', gap: 28, padding: '26px 12px 0', minHeight: 0 }}>
        {bars.map((b) => {
          const clickable = !!onToggle;
          const active = !activeKeys || activeKeys.has(b.key);
          const barColor = active ? b.color : ink(0.22);
          return (
            <div
              key={b.label}
              onClick={clickable ? () => onToggle!(b.key) : undefined}
              style={{
                flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0,
                cursor: clickable ? 'pointer' : 'default',
              }}
            >
              <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                <div
                  title={`${b.label}: ${b.display}`}
                  style={{
                    position: 'absolute', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 130,
                    bottom: `${b.bottom}%`, height: `${Math.max(b.size, 0.8)}%`,
                    borderRadius: b.floating ? 8 : '8px 8px 0 0',
                    // `${b.color}55` (hex-alpha suffix) quebra silenciosamente
                    // quando b.color é uma CSS var() (`var(--color-x)55` não é
                    // uma cor válida — a declaração inteira cai, sem erro no
                    // console) — era por isso que as 4 barras ficavam
                    // invisíveis, só o contorno tracejado das flutuantes
                    // aparecia. Cor sólida + máscara de gradiente pra desvanecer
                    // funciona com qualquer formato de cor, var() incluso.
                    background: barColor,
                    WebkitMaskImage: 'linear-gradient(180deg, #000 55%, transparent)',
                    maskImage: 'linear-gradient(180deg, #000 55%, transparent)',
                    border: b.floating ? `1px dashed ${active ? ink(0.28) : ink(0.16)}` : 'none',
                    opacity: active ? 1 : 0.55,
                    transition: 'height 0.4s ease, bottom 0.4s ease, background 0.2s ease, opacity 0.2s ease',
                  }}
                />
                <div style={{
                  position: 'absolute', left: 0, right: 0, bottom: `calc(${b.bottom + b.size}% + 7px)`,
                  textAlign: 'center', fontSize: 13, fontWeight: 780, color: active ? b.color : ink(0.4),
                  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', transition: 'color 0.2s ease',
                }}>{b.display}</div>
              </div>
              <div style={{
                fontSize: 11, color: active ? ink(0.45) : ink(0.32), marginTop: 9, textAlign: 'center',
                fontWeight: 600, lineHeight: 1.3, transition: 'color 0.2s ease',
              }}>{b.label}</div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: ink(0.32), textAlign: 'center', paddingTop: 12 }}>
        {USD(bruta)} bruto − {USD(anulado)} anulado − {USD(vdi)} VDI = <strong style={{ color: C.accent }}>{USD(confirmada)}</strong> confirmado
      </div>
    </div>
  );
}
